import type { User } from '@/composables/useAuth'
import { differenceInYears } from 'date-fns'
import { serializeDate } from '@/lib/utils/date'

export const TAX_REBATE = 44000
export const TAX_REBATE_SINGLE = 44000
export const TAX_REBATE_MARRIED = 53000
export const DEFAULT_RATE = 0.2
const MAX_TAXBACK = 115000

export const getTaxRelief = (age: number): number =>
  age <= 29
    ? 15
    : age <= 39
      ? 20
      : age <= 49
        ? 25
        : age <= 54
          ? 30
          : age <= 59
            ? 35
            : 40

interface CalculationResult {
  maxPension: number
  pension: number
  maxTaxback: number
  taxback: number
}

interface CalculationParams {
  user: Pick<
    User,
    | 'date_of_birth'
    | 'marital_status'
    | 'annual_income'
    | 'paid_pension'
    | 'taxback'
    | 'pension'
  > | null
  annualIncome?: number
  paidPension?: number
}

export function calculateMaxPensionAndTaxback({
  user,
  annualIncome = user?.annual_income ?? 0,
  paidPension = user?.paid_pension ?? 0,
}: CalculationParams): CalculationResult {
  if (!user) {
    console.log('No user')
    return {
      maxPension: 0,
      maxTaxback: 0,
      pension: 0,
      taxback: 0,
    }
  }
  console.log({ user, annualIncome, paidPension })

  const reliefPercentage = getTaxRelief(
    differenceInYears(new Date(), new Date(serializeDate(user.date_of_birth)!)),
  )

  console.log({ reliefPercentage })

  const taxRebate =
    user.marital_status === 'married' ? TAX_REBATE_MARRIED : TAX_REBATE_SINGLE
  const salary = Math.min(annualIncome, MAX_TAXBACK)
  const contributed = paidPension

  console.log({ salary, contributed })

  // Calculate maximum pension contribution based on age-related percentage
  const MAX_PENSION = Math.floor((salary * reliefPercentage) / 100)
  const remainingContribution = Math.max(MAX_PENSION - contributed, 0)

  // Calculate pension amount
  const maxPension = remainingContribution

  // Calculate tax relief (taxback)
  // For income above the tax rebate threshold, relief is at 40%
  // For income below the tax rebate threshold, relief is at 20%
  const maxTaxback =
    salary > taxRebate
      ? maxPension <= salary - taxRebate
        ? maxPension * 0.4 // All contribution gets 40% relief
        : (salary - taxRebate) * 0.4 + (maxPension - (salary - taxRebate)) * 0.2 // Split between 40% and 20% relief
      : maxPension * 0.2 // All contribution gets 20% relief

  const userPension = user.pension ?? 0
  const taxback =
    salary > taxRebate
      ? userPension <= salary - taxRebate
        ? userPension * 0.4 // All contribution gets 40% relief
        : (salary - taxRebate) * 0.4 +
          (userPension - (salary - taxRebate)) * 0.2 // Split between 40% and 20% relief
      : userPension * 0.2

  return {
    maxPension: Math.abs(maxPension),
    maxTaxback: Math.min(MAX_TAXBACK, Math.abs(maxTaxback)),
    pension: userPension,
    taxback,
  }
}
